import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAuthContext } from '@/context/auth-provider';
import { toast } from '@/hooks/use-toast';
import { mfaEmailToggleMutationFn } from '@/lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import CustomSwitch from '@/components/ui/custom-switch';
import { useNavigate } from 'react-router-dom';
import FormattedMessage from '../formatted-message';
import { Loader } from 'lucide-react';

export default function Email2faAlert() {
    const queryClient = useQueryClient();
    const { emailFA, user } = useAuthContext();
    const navigate = useNavigate();

    const [checked, setChecked] = React.useState<boolean>(false);

    const { mutate, isPending } = useMutation({
        mutationFn: mfaEmailToggleMutationFn,
        onSuccess: (response) => {
            queryClient.invalidateQueries({
                queryKey: ['authUser'],
            });

            toast({
                title: 'Success',
                description: (
                    <FormattedMessage message={response.data.message} />
                ),
                className: 'text-white',
                variant: 'success',
            });
        },
        onError: (error: Error & { errorCode: string }) => {
            toast({
                title: 'Error',
                description: <FormattedMessage message={error.message} />,
                variant: 'destructive',
            });
            if (error.errorCode && error.errorCode === 'ACCESS_UNAUTHORIZED') {
                queryClient.resetQueries({
                    queryKey: ['authUser'],
                });
                navigate('/', { state: {}, replace: true });
            }
        },
    });

    return (
        <div className='flex flex-col gap-5 my-4'>
            <div className='relative sm:pl-6 transition duration-200 ease-in-out'>
                <div className='bg-white dark:bg-background  absolute -left-[9.5px] top-[2.1rem] z-10 block size-6 sm:size-5 rounded-full'>
                    <div className='ml-1 mt-1 size-4 sm:size-3 rounded-full border-2 transition duration-200 ease-in-out border-primary'></div>
                </div>
                <div>
                    <div className='via-root to-root rounded-xl bg-gradient-to-r p-0.5'>
                        <div className='rounded-[10px] bg-accent/75 p-6'>
                            <div className='flex items-center gap-3'>
                                <p className='sm:text-lg tracking-[-0.16px] text-slate-12 font-bold mb-1'>
                                    Email Two-Factor Authentication (2FA)
                                </p>
                            </div>
                            <p className='mb-6 text-sm text-[#0007149f] dark:text-gray-100 font-normal'>
                                By sending a pin code to your primary email to
                                login.
                            </p>
                            <div className='w-full flex justify-start gap-4'>
                                <AlertDialog
                                    open={checked}
                                    onOpenChange={setChecked}>
                                    <AlertDialogTrigger asChild>
                                        <div>
                                            {isPending ? (
                                                <div className='flex items-center'>
                                                    <Loader className='mr-2 animate-spin' />
                                                    Please wait...
                                                </div>
                                            ) : (
                                                <CustomSwitch
                                                    checked={
                                                        emailFA.isEnabled as boolean
                                                    }
                                                />
                                            )}
                                        </div>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>
                                                {emailFA.isEnabled
                                                    ? 'Disable Email 2FA'
                                                    : 'Enable Email 2FA'}
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                {emailFA.isEnabled
                                                    ? `This action will disable Email Two Factor Authentication, 
                               which is not safe and not recommended.`
                                                    : `This action will enable Email Two Factor Authentication,
                                and you will not be able to login without access your
                                email.`}
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>
                                                Cancel
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                                disabled={isPending}
                                                onClick={() => {
                                                    mutate(user.id);
                                                }}>
                                                Continue
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
